import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { History, Loader2, CheckCircle2, XCircle, TrendingUp, TrendingDown, Clock } from "lucide-react";

const RESULT = {
  worked: { color: "#22c55e", Icon: CheckCircle2, it: "Ha funzionato", en: "Worked" },
  could_push: { color: "#0ea5e9", Icon: TrendingUp, it: "Potevi osare di più", en: "Could push more" },
  sold_below: { color: "#f59e0b", Icon: TrendingDown, it: "Margine perso", en: "Lost margin" },
  unsold: { color: "#ef4444", Icon: XCircle, it: "Non venduta", en: "Unsold" },
  superseded: { color: "#ef4444", Icon: XCircle, it: "Non ha funzionato (sostituito)", en: "Didn't work (superseded)" },
  pre_booked: { color: "#8b5cf6", Icon: Clock, it: "Già prenotata prima", en: "Booked before" },
  pending: { color: "#94a3b8", Icon: Clock, it: "In attesa", en: "Pending" },
};

export default function AppliedHistory({ propId, lang = "it" }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/applied-outcomes?lang=${lang}`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [propId, lang]);

  useEffect(() => { load(); }, [load]);

  const Chip = ({ k, n }) => {
    const r = RESULT[k] || RESULT.pending;
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
        style={{ backgroundColor: `color-mix(in srgb, ${r.color} 15%, transparent)`, color: r.color }} data-testid={`applied-chip-${k}`}>
        <r.Icon className="w-3.5 h-3.5" />{L(r.it, r.en)} · {n}
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-lg p-6" data-testid="applied-history">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">{L("Storico Applicati & Esiti", "Applied History & Outcomes")}</h2>
        </div>
        {data?.accuracy != null && (
          <div className="text-sm text-muted-foreground" data-testid="applied-accuracy">
            {L("Consigli riusciti", "Successful advice")}: <b className="text-foreground">{data.accuracy}%</b> ({data.worked}/{data.evaluated})
          </div>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {L("Ogni prezzo segnato come «Applicato» e il suo esito reale. Il confronto è NETTO vs NETTO: il prezzo applicato viene riportato al netto della tassa di soggiorno e della tua % di offerte, e confrontato con la camera più economica realmente venduta DOPO la tua decisione (rilevata dai file che importi).",
          "Every price marked \"Applied\" and its real outcome. Comparison is NET vs NET: the applied price is shown net of city tax and your offers %, compared with the cheapest room actually sold AFTER your decision (detected from the files you import).")}
      </p>

      {loading ? (
        <div className="py-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : !data || data.total_applied === 0 ? (
        <p className="text-sm text-muted-foreground py-4">
          {L("Nessun prezzo applicato ancora. Usa il pulsante «Applicato» nella Dashboard per registrare le tue scelte.",
            "No applied price yet. Use the \"Applied\" button in the Dashboard to record your choices.")}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(data.summary || {}).map(([k, n]) => <Chip key={k} k={k} n={n} />)}
          </div>
          <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
            <table className="w-full text-sm" data-testid="applied-history-table">
              <thead>
                <tr className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-bold px-3 py-2">{L("Data", "Date")}</th>
                  <th className="text-right font-bold px-3 py-2">{L("Applicato (netto)", "Applied (net)")}</th>
                  <th className="text-right font-bold px-3 py-2">{L("Venduto netto (min)", "Sold net (min)")}</th>
                  <th className="text-right font-bold px-3 py-2" title={L("Camere occupate quando hai applicato il prezzo", "Rooms occupied when you applied the price")}>{L("Occ. all'applicazione", "Occ. at apply")}</th>
                  <th className="text-right font-bold px-3 py-2" title={L("Camere occupate ora (si aggiorna ad ogni file importato)", "Rooms occupied now (updates on each imported file)")}>{L("Occ. ora", "Occ. now")}</th>
                  <th className="text-left font-bold px-3 py-2">{L("Esito", "Outcome")}</th>
                </tr>
              </thead>
              <tbody>
                {(data.items || []).map((r, i) => {
                  const res = RESULT[r.result] || RESULT.pending;
                  return (
                    <tr key={r.id || r.date} data-testid={`applied-row-${i}`} className="border-t border-border hover:bg-accent/20">
                      <td className="px-3 py-2 font-data">{r.date}</td>
                      <td className="px-3 py-2 text-right font-data font-semibold" title={r.applied_price_gross != null ? `${L("lordo Booking", "Booking gross")} €${Math.round(r.applied_price_gross)}` : ""}>{r.applied_price != null ? `€${Math.round(r.applied_price)}` : "—"}</td>
                      <td className="px-3 py-2 text-right font-data">{r.sold_adr != null ? `€${Math.round(r.sold_adr)}` : "—"}</td>
                      <td className="px-3 py-2 text-right font-data text-muted-foreground" data-testid={`applied-occ-apply-${i}`}>{r.rooms_occupied_at_apply != null ? `${r.rooms_occupied_at_apply}${r.rooms_total ? `/${r.rooms_total}` : ""}` : "—"}</td>
                      <td className="px-3 py-2 text-right font-data font-semibold" data-testid={`applied-occ-now-${i}`}>
                        {r.rooms_occupied_now != null ? `${r.rooms_occupied_now}${r.rooms_total ? `/${r.rooms_total}` : ""}` : "—"}
                        {r.rooms_occupied_at_apply != null && r.rooms_occupied_now != null && r.rooms_occupied_now > r.rooms_occupied_at_apply && (
                          <span className="ml-1 text-[10px] font-bold" style={{ color: "#16a34a" }}>+{r.rooms_occupied_now - r.rooms_occupied_at_apply}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-start gap-1.5">
                          <res.Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: res.color }} />
                          <span className="text-xs text-muted-foreground">{r.verdict}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </motion.div>
  );
}
